/**
 * AutoMapa vehicle risk constants (Milestone 10).
 *
 * Buyer-safe language only — avoid: fraude, estafa, peligroso, no compres, falso.
 */
export { RISK_ANALYTICS_EVENTS } from "./automapAnalyticsEvents";

export const RISK_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  UNAVAILABLE: "unavailable",
  UNKNOWN: "unknown",
};

export const RISK_LEVEL_LABELS_ES = {
  low: "Riesgo bajo",
  medium: "Revisar señales",
  high: "Revisar con cuidado",
  unavailable: "No disponible",
  unknown: "Sin evaluación",
};

export const RISK_LEVEL_SHORT_LABELS_ES = {
  low: "Riesgo bajo",
  medium: "Revisar señales",
  high: "Revisar con cuidado",
  unavailable: "No disponible",
  unknown: "Sin evaluación",
};

export const RISK_LEVEL_TOOLTIPS_ES = {
  low: "No vemos señales importantes con la información disponible. Confirma datos antes de comprar.",
  medium: "Hay información que conviene revisar antes de comprar.",
  high: "Hay señales que ameritan más preguntas antes de avanzar. Confirma información clave.",
  unavailable: "Este vehículo parece no estar disponible actualmente.",
  unknown: "No tenemos suficiente información para mostrar señales de revisión.",
};

export const RISK_DETAIL_TITLES_ES = {
  low: "No vemos señales importantes con la información disponible",
  medium: "Hay algunas señales que conviene revisar",
  high: "Revisa esta publicación con cuidado",
  unavailable: "Publicación no disponible",
  unknown: "Sin evaluación disponible",
};

export const RISK_DETAIL_COPY_ES = {
  low: "Aun así, confirma disponibilidad, documentos, kilometraje y estado del vehículo antes de comprar.",
  medium:
    "No significa que el vehículo tenga un problema, pero sí hay datos que deberías confirmar con el concesionario.",
  high: "AutoMapa detectó señales que ameritan más preguntas antes de avanzar. Confirma información clave y considera un peritaje.",
  unavailable: "Esta publicación ya no parece estar disponible para compra.",
  unknown: "No tenemos suficiente información para mostrar señales de revisión en esta publicación.",
};

export const RISK_DISCLAIMER_ES =
  "Estas señales no son acusaciones ni garantías. Úsalas como preguntas para hacer antes de comprar.";

export const RISK_METHODOLOGY_ES =
  "AutoMapa revisa frescura, completitud y coherencia de la publicación. No reemplaza inspección mecánica ni revisión documental.";

export const RISK_LEVEL_CLASS = {
  low: "green",
  medium: "amber",
  high: "red",
  unavailable: "gray",
  unknown: "gray",
};

export const RISK_FLAGS = {
  STALE_LISTING: "stale_listing",
  AVAILABILITY_NOT_CONFIRMED: "availability_not_confirmed",
  MISSING_MILEAGE: "missing_mileage",
  MISSING_PRICE: "missing_price",
  MISSING_PHOTOS: "missing_photos",
  FEW_PHOTOS: "few_photos",
  PRICE_OUTLIER_HIGH: "price_outlier_high",
  PRICE_OUTLIER_LOW: "price_outlier_low",
  HIGH_MILEAGE_FOR_YEAR: "high_mileage_for_year",
  DEALER_UNVERIFIED: "dealer_unverified",
  DEALER_CONTACT_MISSING: "dealer_contact_missing",
  LOCATION_MISSING: "location_missing",
  DUPLICATE_CANDIDATE: "duplicate_candidate",
  IMPORTED_DATA_INCOMPLETE: "imported_data_incomplete",
  TRUST_SAFETY_REPORT_OPEN: "trust_safety_report_open",
  LISTING_QUALITY_LOW: "listing_quality_low",
  UNKNOWN_HISTORY: "unknown_history",
  NO_RISK_DATA: "no_risk_data",
};

export const RISK_FLAG_LABELS_ES = {
  stale_listing: "Publicación sin actualización reciente",
  availability_not_confirmed: "Disponibilidad por confirmar",
  missing_mileage: "Kilometraje no informado",
  missing_price: "Precio no informado",
  missing_photos: "Sin fotos",
  few_photos: "Pocas fotos",
  price_outlier_high: "Precio alto frente a comparables",
  price_outlier_low: "Precio muy bajo frente a comparables",
  high_mileage_for_year: "Kilometraje alto para el año",
  dealer_unverified: "Concesionario no verificado",
  dealer_contact_missing: "Contacto incompleto",
  location_missing: "Ubicación por confirmar",
  duplicate_candidate: "Posible duplicado",
  imported_data_incomplete: "Datos incompletos",
  trust_safety_report_open: "Reporte en revisión",
  listing_quality_low: "Información limitada",
  unknown_history: "Historial por confirmar",
  no_risk_data: "Sin evaluación disponible",
};

export const RISK_FLAG_DESCRIPTIONS_ES = {
  stale_listing: "La disponibilidad puede cambiar rápido.",
  availability_not_confirmed: "El concesionario aún no confirmó disponibilidad reciente.",
  missing_mileage: "El kilometraje ayuda a comparar desgaste y precio.",
  missing_price: "Sin precio publicado es difícil comparar opciones.",
  missing_photos: "Las fotos ayudan a evaluar el estado general del vehículo.",
  few_photos: "Pocas fotos limitan la revisión visual antes de visitar.",
  price_outlier_high: "El precio parece alto frente a publicaciones similares.",
  price_outlier_low: "Un precio muy bajo puede requerir más preguntas sobre el estado.",
  high_mileage_for_year: "El kilometraje parece alto para el año del vehículo.",
  dealer_unverified: "AutoMapa aún no ha completado la verificación de este vendedor.",
  dealer_contact_missing: "No hay WhatsApp ni teléfono publicado para contactar.",
  location_missing: "La ubicación ayuda a planificar visitas y comparar opciones.",
  duplicate_candidate: "Esta publicación podría repetir otra ya vista.",
  imported_data_incomplete: "Faltan datos clave en la publicación.",
  trust_safety_report_open: "Esta publicación tiene información en revisión.",
  listing_quality_low: "La publicación tiene información limitada para comparar.",
  unknown_history: "No hay historial suficiente en la publicación.",
  no_risk_data: "No hay señales de revisión disponibles.",
};

export const RISK_FLAG_QUESTIONS_ES = {
  stale_listing: "¿El vehículo sigue disponible y el precio sigue vigente?",
  availability_not_confirmed: "¿El vehículo sigue disponible?",
  missing_mileage: "¿Cuál es el kilometraje actual? ¿Puedes enviar foto del odómetro?",
  missing_price: "¿Cuál es el precio actual y qué incluye?",
  missing_photos: "¿Puedes enviar fotos adicionales del vehículo?",
  few_photos: "¿Puedes enviar más fotos del interior, motor y llantas?",
  price_outlier_high: "¿El precio incluye algún beneficio o está abierto a negociación?",
  price_outlier_low: "¿Por qué el precio es más bajo que opciones similares? ¿Hay detalles a revisar?",
  high_mileage_for_year: "¿El kilometraje corresponde al uso del vehículo?",
  dealer_unverified: "¿Puedes compartir datos del concesionario, dirección y documentos antes de separar?",
  dealer_contact_missing: "¿Cuál es el mejor medio para contactarte?",
  location_missing: "¿En qué ciudad está el vehículo y dónde se puede ver?",
  duplicate_candidate: "¿Esta es la publicación activa del vehículo?",
  imported_data_incomplete: "¿Puedes completar marca, modelo, año, precio y fotos?",
  trust_safety_report_open: "¿Puedes confirmar el estado actual de la publicación?",
  listing_quality_low: "¿Puedes confirmar los datos principales del vehículo?",
  unknown_history: "¿Tiene historial de accidentes o reclamaciones?",
  no_risk_data: "¿Puedes confirmar disponibilidad, kilometraje y documentos?",
};

export const RECOMMENDATION_RISK_ADJUSTMENTS = {
  low: 5,
  medium: -5,
  high: -20,
  unavailable: null,
  unknown: -5,
};

export function trackVehicleRiskEvent(eventType, metadata = {}) {
  const { trackVehicleRiskEvent: track } = require("../utils/vehicleRiskAnalytics");
  return track(eventType, metadata);
}
